import { AnimatePresence, motion } from "framer-motion";
import { FaExternalLinkAlt } from "react-icons/fa";
import { useTheme } from "../../contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
// @ts-ignore
import certImage from "../../assets/certifications/Charcrit_Sreecharoen_Cert_Preview.png";
// @ts-ignore
import ciscoImage from "../../assets/certifications/cisco.png";
import event1 from "../../assets/events/event_image_1.jpg";
import { fetchCertifications, fetchEvents, type Certification as APICertification, type Event as APIEvent } from "../../lib/api";
import { CardRail } from "../ui/CardRail";
import { Card } from "../ui/quick-look/card";
import { monoInk } from "../../lib/mono";

type Tab = "certs" | "events";

interface Certification {
  title: string;
  issuer: string;
  date: string;
  description: string;
  credentialUrl?: string;
  image?: string;
}

interface EventItem {
  title: string;
  date: string;
  location: string;
  description: string;
  image?: string;
  link?: string;
}

function mapApiCertification(cert: APICertification): Certification {
  return {
    title: cert.title,
    issuer: cert.issuer,
    date: cert.date,
    description: cert.description || "",
    credentialUrl: cert.credentialUrl || undefined,
    image: cert.imageUrl || undefined,
  };
}

function mapApiEvent(event: APIEvent): EventItem {
  return {
    title: event.title,
    date: event.date,
    location: event.location,
    description: event.description || "",
    image: event.imageUrl || undefined,
    link: event.link || undefined,
  };
}

const FALLBACK_CERTIFICATIONS: Certification[] = [
  {
    title: "AXTRA MILE HACKATHON 2025 Certification",
    issuer: "CP AXTRA , makro , Lotus's",
    date: "1 Aug 2025",
    description:
      "Successfully completed the AXTRA MILE HACKATHON 2025, demonstrating skills in innovative problem-solving and teamwork.",
    credentialUrl: certImage,
    image: certImage,
  },
  {
    title: "Getting started with Cisco Packet Tracer",
    issuer: "CISCO",
    date: "4 Feb 2026",
    description: "By networking academy through Cisco Networking Academy program.",
    image: ciscoImage,
  },
];

const FALLBACK_EVENTS: EventItem[] = [
  {
    title: "CoSI จัดอบรมบุคลากรทางการแพทย์ของโรงพยาบาลสินแพทย์",
    date: "12 November 2025",
    location: "Bangkok, Thailand",
    description:
      "ศูนย์นวัตกรรมเฉพาะทาง (CoSI) มหาวิทยาลัยกรุงเทพ นำทีมนักวิจัยจัดอบรมบุคลากรทางการแพทย์ของโรงพยาบาลสินแพทย์ ภายใต้โครงการ 'แพลตฟอร์มการฝึกอบรมทักษะด้านการแพทย์และพยาบาล' ซึ่งเป็นแพลตฟอร์มที่ออกแบบและพัฒนาโดยศูนย์ CoSI เพื่อพัฒนาศักยภาพและเสริมสร้างทักษะทางการแพทย์ที่ทันสมัย พร้อมยกระดับคุณภาพการบริการสุขภาพอย่างยั่งยืน",
    image: event1,
  },
];

function CardMedia({ src, alt, cover }: { src?: string; alt: string; cover: boolean }) {
  if (!src) {
    return (
      <svg
        className="h-10 w-10"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
      </svg>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      loading="lazy"
      className={cover ? "h-full w-full object-cover" : "h-full w-full object-contain p-4"}
    />
  );
}

function QuickFacts({
  rows,
  href,
  hrefLabel,
}: {
  rows: { label: string; value: string }[];
  href?: string;
  hrefLabel?: string;
}) {
  return (
    <div className="flex flex-col gap-3 text-[13px] leading-snug">
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5">
        {rows.map((row) => (
          <div key={row.label} className="contents">
            <dt className="opacity-60">{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-medium underline underline-offset-4"
        >
          {hrefLabel}
          <FaExternalLinkAlt className="h-3 w-3" aria-hidden />
        </a>
      ) : null}
    </div>
  );
}

function LinkAction({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full text-inherit opacity-70"
    >
      <FaExternalLinkAlt className="h-3.5 w-3.5" />
    </a>
  );
}

function QuickCard({
  title,
  description,
  image,
  cover,
  meta,
  status,
  action,
  details,
  open,
  onOpenChange,
}: {
  title: string;
  description: string;
  image?: string;
  cover: boolean;
  meta: ReactNode;
  status: string;
  action?: ReactNode;
  details: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Card
      title={title}
      description={description || undefined}
      media={<CardMedia src={image} alt={title} cover={cover} />}
      meta={meta}
      status={status}
      action={action}
      details={details}
      open={open}
      onOpenChange={onOpenChange}
    />
  );
}

export const Certifications = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const isDark = theme === "dark";
  const [activeTab, setActiveTab] = useState<Tab>("certs");
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [openKey, setOpenKey] = useState<string | null>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const trackRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState({ left: 0, width: 0 });

  const tones = monoInk(isDark);
  const ink = tones.ink;
  const muted = tones.muted;

  useEffect(() => {
    const load = async () => {
      try {
        const [certsData, eventsData] = await Promise.all([
          fetchCertifications(),
          fetchEvents(),
        ]);
        setCertifications(certsData.map(mapApiCertification));
        setEvents(eventsData.map(mapApiEvent));
      } catch {
        setCertifications(FALLBACK_CERTIFICATIONS);
        setEvents(FALLBACK_EVENTS);
      }
    };
    load();
  }, []);

  const CERTIFICATIONS =
    certifications.length > 0 ? certifications : FALLBACK_CERTIFICATIONS;
  const EVENTS = events.length > 0 ? events : FALLBACK_EVENTS;

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: "certs", label: t("cert.filter.certs"), count: CERTIFICATIONS.length },
    { id: "events", label: t("cert.filter.events"), count: EVENTS.length },
  ];

  const updatePill = useCallback(() => {
    const idx = activeTab === "certs" ? 0 : 1;
    const btn = tabRefs.current[idx];
    const track = trackRef.current;
    if (!btn || !track) return;
    const trackBox = track.getBoundingClientRect();
    const btnBox = btn.getBoundingClientRect();
    setPill({ left: btnBox.left - trackBox.left, width: btnBox.width });
  }, [activeTab]);

  useEffect(() => {
    // wait a frame so tab labels/layout settle
    const id = requestAnimationFrame(updatePill);
    window.addEventListener("resize", updatePill);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", updatePill);
    };
  }, [updatePill, CERTIFICATIONS.length, EVENTS.length]);

  const credsLabel = t("cert.showcreds");
  const swipeHint = t("cert.swipehint");

  return (
    <section
      id="certifications"
      className={[
        "apple-tile overflow-x-clip",
        isDark ? "apple-tile-dark" : "apple-tile-light",
      ].join(" ")}
    >
      <div className="apple-container-wide space-y-6 sm:space-y-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55 }}
          className="text-center px-1"
        >
          <h2 className="apple-title" style={{ color: ink }}>
            {t("cert.title1")}
            {t("cert.title2")}
          </h2>
        </motion.div>

        {/* Segmented control — full-width on mobile */}
        <div className="flex justify-center px-0 sm:px-1 sticky top-[calc(3.5rem+env(safe-area-inset-top)+var(--site-frame))] sm:static z-20">
          <div
            ref={trackRef}
            className="relative grid grid-cols-2 w-full max-w-md p-1 rounded-full"
            style={{
              background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
              backdropFilter: "saturate(180%) blur(16px)",
              WebkitBackdropFilter: "saturate(180%) blur(16px)",
            }}
            role="tablist"
          >
            <motion.div
              className="absolute top-1 bottom-1 rounded-full pointer-events-none"
              style={{
                left: pill.left,
                width: pill.width,
                background: isDark ? "#f5f5f5" : "#171717",
              }}
              initial={false}
              animate={{ left: pill.left, width: pill.width }}
              transition={{ type: "spring", stiffness: 380, damping: 34 }}
            />
            {tabs.map((tab, i) => {
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    setOpenKey(null);
                    setActiveTab(tab.id);
                  }}
                  className="relative z-10 min-h-[44px] px-3 sm:px-5 rounded-full text-[13px] sm:text-[14px] transition-colors whitespace-nowrap"
                  style={{
                    color: active
                      ? isDark
                        ? "#171717"
                        : "#ffffff"
                      : muted,
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {tab.label}
                  <span className="ml-1.5 opacity-75 tabular-nums">{tab.count}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="relative">
          <AnimatePresence mode="wait" initial={false}>
            {activeTab === "certs" ? (
              <motion.div
                key="certs"
                role="tabpanel"
                initial={{ opacity: 0, x: 28 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -28 }}
                transition={{ type: "spring", stiffness: 340, damping: 36 }}
              >
                <CardRail count={CERTIFICATIONS.length} ink={ink}>
                  {CERTIFICATIONS.map((cert, idx) => {
                    const key = `cert-${idx}-${cert.title}`;
                    return (
                      <QuickCard
                        key={key}
                        title={cert.title}
                        description={cert.description}
                        image={cert.image}
                        cover={false}
                        meta={cert.issuer}
                        status={cert.date}
                        action={cert.credentialUrl ? <LinkAction href={cert.credentialUrl} label={credsLabel} /> : undefined}
                        details={
                          <QuickFacts
                            rows={[
                              { label: t("cert.field.issuer"), value: cert.issuer },
                              { label: t("cert.field.date"), value: cert.date },
                            ]}
                            href={cert.credentialUrl}
                            hrefLabel={credsLabel}
                          />
                        }
                        open={openKey === key}
                        onOpenChange={(next) => setOpenKey(next ? key : null)}
                      />
                    );
                  })}
                </CardRail>
              </motion.div>
            ) : (
              <motion.div
                key="events"
                role="tabpanel"
                initial={{ opacity: 0, x: 28 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -28 }}
                transition={{ type: "spring", stiffness: 340, damping: 36 }}
              >
                <CardRail count={EVENTS.length} ink={ink}>
                  {EVENTS.map((event, idx) => {
                    const key = `event-${idx}-${event.title}`;
                    return (
                      <QuickCard
                        key={key}
                        title={event.title}
                        description={event.description}
                        image={event.image}
                        cover
                        meta={event.location}
                        status={event.date}
                        action={event.link ? <LinkAction href={event.link} label={event.title} /> : undefined}
                        details={
                          <QuickFacts
                            rows={[
                              { label: t("cert.field.date"), value: event.date },
                              { label: t("cert.field.location"), value: event.location },
                            ]}
                            href={event.link}
                            hrefLabel={event.title}
                          />
                        }
                        open={openKey === key}
                        onOpenChange={(next) => setOpenKey(next ? key : null)}
                      />
                    );
                  })}
                </CardRail>
              </motion.div>
            )}
          </AnimatePresence>

          <p
            className="md:hidden text-center text-[11px] mt-3 opacity-60"
            style={{ color: muted }}
          >
            {swipeHint}
          </p>
        </div>
      </div>
    </section>
  );
};
